import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, contractorsAPI, aiAPI, quotesAPI, suppliersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Search, Edit, Trash2, Briefcase, MapPin, Calendar, DollarSign,
  ChevronRight, Clock, CheckCircle, AlertCircle, PauseCircle, XCircle, Activity, Receipt,
  Sparkles, Wand2, Loader2, Building2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Activity },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  on_hold: { label: 'On Hold', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: PauseCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  invoiced: { label: 'Invoiced', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Receipt },
};

const statusOptions = ['pending', 'in_progress', 'completed', 'on_hold', 'cancelled', 'invoiced'];

// Urgency colour coding based on the on-site (booking) date
const getUrgency = (job) => {
  if (!job.on_site_date || ['completed', 'cancelled', 'invoiced'].includes(job.status)) {
    return { level: 'none', border: 'border-l-transparent' };
  }
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(job.on_site_date); d.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return { level: 'overdue', label: `Overdue ${Math.abs(days)}d`, border: 'border-l-red-500', badge: 'bg-red-500/20 text-red-400 border-red-500/40', dot: 'bg-red-500' };
  if (days === 0) return { level: 'today', label: 'On site today', border: 'border-l-orange-500', badge: 'bg-orange-500/20 text-orange-400 border-orange-500/40', dot: 'bg-orange-500' };
  if (days <= 2) return { level: 'soon', label: `In ${days}d`, border: 'border-l-amber-400', badge: 'bg-amber-400/20 text-amber-400 border-amber-400/40', dot: 'bg-amber-400' };
  if (days <= 7) return { level: 'week', label: `In ${days}d`, border: 'border-l-cyan-400', badge: 'bg-cyan-400/15 text-cyan-300 border-cyan-400/30', dot: 'bg-cyan-400' };
  return { level: 'future', label: `In ${days}d`, border: 'border-l-emerald-400/50', badge: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/25', dot: 'bg-emerald-400' };
};


export const JobsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    start_date: '',
    end_date: '',
    budget: '',
    supplier_id: '',
    supplier_name: '',
    po_reference: '',
    on_site_date: '',
  });
  
  // AI Job Creation state
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiSuggestedQuote, setAiSuggestedQuote] = useState(null);
  const [quotePreviewOpen, setQuotePreviewOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, contractorsRes, suppliersRes] = await Promise.all([
        jobsAPI.getAll(),
        user?.role !== 'trade' ? contractorsAPI.getAll() : Promise.resolve({ data: [] }),
        user?.role !== 'trade' ? suppliersAPI.getAll() : Promise.resolve({ data: [] })
      ]);
      setJobs(jobsRes.data);
      setContractors(contractorsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
      };

      if (editingJob) {
        await jobsAPI.update(editingJob.id, data);
        toast.success('Job updated');
      } else {
        const jobResponse = await jobsAPI.create(data);
        const newJobId = jobResponse.data?.id;
        toast.success('Job created');
        
        // If we have an AI-suggested quote, create it for this job
        if (aiSuggestedQuote && newJobId) {
          try {
            // Flatten sections to items for API
            const allItems = [];
            aiSuggestedQuote.sections?.forEach(section => {
              section.items?.forEach(item => {
                if (item.description) {
                  allItems.push({
                    ...item,
                    section: 'custom',
                    section_name: section.section_name
                  });
                }
              });
            });
            
            if (allItems.length > 0) {
              await quotesAPI.create({
                job_id: newJobId,
                client_name: data.client_name,
                client_email: data.client_email,
                sections: aiSuggestedQuote.sections,
                items: allItems
              });
              toast.success('AI-generated quote also created!');
            }
          } catch (quoteError) {
            console.error('Failed to create AI-suggested quote:', quoteError);
            toast.info('Job created, but quote generation failed - you can create one manually');
          }
          setAiSuggestedQuote(null);
        }
      }
      
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (e, job) => {
    e.stopPropagation();
    setEditingJob(job);
    setFormData({
      title: job.title,
      description: job.description || '',
      client_name: job.client_name,
      client_email: job.client_email || '',
      client_phone: job.client_phone || '',
      address: job.address,
      start_date: job.start_date || '',
      end_date: job.end_date || '',
      budget: job.budget?.toString() || '',
      supplier_id: job.supplier_id || '',
      supplier_name: job.supplier_name || '',
      po_reference: job.po_reference || '',
      on_site_date: job.on_site_date || '',
    });
    setDialogOpen(true);
  };

  const handleStatusChange = async (e, jobId, newStatus) => {
    e.stopPropagation();
    try {
      await jobsAPI.updateStatus(jobId, newStatus);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await jobsAPI.delete(id);
      toast.success('Job deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingJob(null);
    setFormData({
      title: '',
      description: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      address: '',
      start_date: '',
      end_date: '',
      budget: '',
      supplier_id: '',
      supplier_name: '',
      po_reference: '',
      on_site_date: '',
    });
  };

  // AI Job Extraction
  const handleAIExtract = async () => {
    if (!aiDescription.trim()) {
      toast.error('Please enter a job description');
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await aiAPI.createJob({ description: aiDescription });
      setAiSuggestion(response.data);
      toast.success('AI extracted job details!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI extraction failed');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestion = () => {
    if (!aiSuggestion) return;
    
    setFormData({
      title: aiSuggestion.title || '',
      description: aiSuggestion.description || '',
      client_name: aiSuggestion.client_name || '',
      client_email: aiSuggestion.client_email || '',
      client_phone: aiSuggestion.client_phone || '',
      address: aiSuggestion.address || '',
      start_date: '',
      end_date: '',
      budget: aiSuggestion.estimated_value?.toString() || '',
    });
    
    // Store the suggested quote for creation after job is saved
    if (aiSuggestion.suggested_quote) {
      setAiSuggestedQuote(aiSuggestion.suggested_quote);
    }
    
    // Close AI dialog and open create dialog
    setAiDialogOpen(false);
    setAiSuggestion(null);
    setAiDescription('');
    setDialogOpen(true);
    toast.success('Job details applied! Review and save to also create the quote.');
  };

  const resetAIDialog = () => {
    setAiDialogOpen(false);
    setAiDescription('');
    setAiSuggestion(null);
    setAiSuggestedQuote(null);
  };

  // Filter out invoiced jobs - they show in Invoicing page
  const filteredJobs = jobs.filter(job => {
    // Hide invoiced jobs from main jobs list
    if (job.status === 'invoiced') return false;
    
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getContractorNames = (ids) => {
    if (!ids || ids.length === 0) return '';
    return ids.map(id => contractors.find(c => c.id === id)?.name || 'Unknown').join(', ');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Jobs</h1>
          <p className="text-muted-foreground mt-1">Manage your construction projects</p>
        </div>
        {user?.role !== 'trade' && (
          <div className="flex gap-2">
            {/* AI Job Creation Dialog */}
            <Dialog open={aiDialogOpen} onOpenChange={(open) => { if (!open) resetAIDialog(); else setAiDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-lg border-primary/50 hover:border-primary" data-testid="ai-create-job-btn">
                  <Sparkles className="h-4 w-4 mr-2 text-primary" />
                  AI Create
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Wand2 className="h-5 w-5 text-primary" />
                    AI Job Creation
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Paste any text description (email, notes, quote request) and AI will extract job details automatically.
                  </p>
                  <Textarea
                    value={aiDescription}
                    onChange={(e) => setAiDescription(e.target.value)}
                    placeholder="e.g., John Smith needs a bathroom renovation at 45 George St Brisbane. He wants new tiles, vanity, and shower. Budget around $15k..."
                    className="bg-input border-border rounded-lg min-h-[120px]"
                    data-testid="ai-description-input"
                  />
                  
                  {aiSuggestion && (
                    <Card className="bg-primary/5 border-primary/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Extracted Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div><span className="text-muted-foreground">Title:</span> {aiSuggestion.title}</div>
                        <div><span className="text-muted-foreground">Client:</span> {aiSuggestion.client_name || 'Not specified'}</div>
                        <div><span className="text-muted-foreground">Address:</span> {aiSuggestion.address || 'Not specified'}</div>
                        <div><span className="text-muted-foreground">Est. Value:</span> ${aiSuggestion.estimated_value?.toLocaleString() || '0'}</div>
                        <div><span className="text-muted-foreground">Type:</span> {aiSuggestion.job_type}</div>
                        {aiSuggestion.suggested_trades?.length > 0 && (
                          <div><span className="text-muted-foreground">Trades Needed:</span> {aiSuggestion.suggested_trades.join(', ')}</div>
                        )}
                        {aiSuggestion.suggested_quote?.sections?.length > 0 && (
                          <div className="pt-2 border-t border-primary/20 mt-2">
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground font-medium">Quote Preview:</span>
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="h-auto p-0 text-primary"
                                onClick={() => setQuotePreviewOpen(true)}
                              >
                                Edit Quote →
                              </Button>
                            </div>
                            <div className="mt-1 space-y-1 max-h-24 overflow-y-auto text-xs">
                              {aiSuggestion.suggested_quote.sections.map((section, idx) => {
                                const sectionTotal = section.items?.reduce((sum, item) => sum + (item.quantity || 1) * (item.unit_price || 0), 0) || 0;
                                return (
                                  <div key={idx} className="bg-background/50 px-2 py-1 rounded flex justify-between">
                                    <span><span className="font-medium">{section.section_name}</span>: {section.items?.length || 0} items</span>
                                    <span className="font-mono">${sectionTotal.toLocaleString()}</span>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="flex justify-between items-center mt-2 pt-1 border-t border-primary/10">
                              <p className="text-xs text-green-600">✓ Quote will be auto-created</p>
                              <span className="text-xs font-mono font-bold">
                                Total: ${aiSuggestion.suggested_quote.sections.reduce((total, section) => 
                                  total + (section.items?.reduce((sum, item) => sum + (item.quantity || 1) * (item.unit_price || 0), 0) || 0), 0
                                ).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={resetAIDialog} className="rounded-lg">
                      Cancel
                    </Button>
                    {!aiSuggestion ? (
                      <Button 
                        onClick={handleAIExtract} 
                        disabled={aiLoading || !aiDescription.trim()}
                        className="rounded-lg"
                        data-testid="ai-extract-btn"
                      >
                        {aiLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <Wand2 className="h-4 w-4 mr-2" />
                            Extract Details
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button onClick={applyAISuggestion} className="rounded-lg bg-green-600 hover:bg-green-700" data-testid="ai-apply-btn">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Use These Details
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Quote Preview/Edit Dialog */}
            <Dialog open={quotePreviewOpen} onOpenChange={setQuotePreviewOpen}>
              <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Edit AI-Generated Quote
                  </DialogTitle>
                </DialogHeader>
                {aiSuggestion?.suggested_quote && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Review and edit the quote items before creating the job. Changes will be saved when you create the job.
                    </p>
                    
                    {aiSuggestion.suggested_quote.sections.map((section, sectionIdx) => (
                      <div key={sectionIdx} className="border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <Input
                            value={section.section_name}
                            onChange={(e) => {
                              const updated = { ...aiSuggestion };
                              updated.suggested_quote.sections[sectionIdx].section_name = e.target.value;
                              setAiSuggestion(updated);
                            }}
                            className="bg-input border-border rounded-lg font-semibold max-w-xs"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = { ...aiSuggestion };
                              updated.suggested_quote.sections.splice(sectionIdx, 1);
                              setAiSuggestion(updated);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-1">
                            <div className="col-span-5">Description</div>
                            <div className="col-span-2">Trade</div>
                            <div className="col-span-1">Qty</div>
                            <div className="col-span-2">Price</div>
                            <div className="col-span-1">Total</div>
                            <div className="col-span-1"></div>
                          </div>
                          
                          {section.items?.map((item, itemIdx) => (
                            <div key={itemIdx} className="grid grid-cols-12 gap-2 items-center">
                              <Input
                                value={item.description}
                                onChange={(e) => {
                                  const updated = { ...aiSuggestion };
                                  updated.suggested_quote.sections[sectionIdx].items[itemIdx].description = e.target.value;
                                  setAiSuggestion(updated);
                                }}
                                className="col-span-5 bg-input border-border rounded-lg text-sm"
                              />
                              <Input
                                value={item.trade_type}
                                onChange={(e) => {
                                  const updated = { ...aiSuggestion };
                                  updated.suggested_quote.sections[sectionIdx].items[itemIdx].trade_type = e.target.value;
                                  setAiSuggestion(updated);
                                }}
                                className="col-span-2 bg-input border-border rounded-lg text-sm"
                              />
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => {
                                  const updated = { ...aiSuggestion };
                                  updated.suggested_quote.sections[sectionIdx].items[itemIdx].quantity = parseFloat(e.target.value) || 0;
                                  setAiSuggestion(updated);
                                }}
                                className="col-span-1 bg-input border-border rounded-lg text-sm"
                              />
                              <Input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => {
                                  const updated = { ...aiSuggestion };
                                  updated.suggested_quote.sections[sectionIdx].items[itemIdx].unit_price = parseFloat(e.target.value) || 0;
                                  setAiSuggestion(updated);
                                }}
                                className="col-span-2 bg-input border-border rounded-lg text-sm"
                              />
                              <div className="col-span-1 text-right font-mono text-sm">
                                ${((item.quantity || 0) * (item.unit_price || 0)).toLocaleString()}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const updated = { ...aiSuggestion };
                                  updated.suggested_quote.sections[sectionIdx].items.splice(itemIdx, 1);
                                  setAiSuggestion(updated);
                                }}
                                className="col-span-1"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const updated = { ...aiSuggestion };
                              updated.suggested_quote.sections[sectionIdx].items.push({
                                description: '',
                                quantity: 1,
                                unit_price: 0,
                                trade_type: 'General',
                                unit: 'Item'
                              });
                              setAiSuggestion(updated);
                            }}
                            className="mt-2"
                          >
                            <Plus className="h-4 w-4 mr-1" /> Add Item
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        const updated = { ...aiSuggestion };
                        updated.suggested_quote.sections.push({
                          section_name: 'New Section',
                          items: [{ description: '', quantity: 1, unit_price: 0, trade_type: 'General', unit: 'Item' }]
                        });
                        setAiSuggestion(updated);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Section
                    </Button>
                    
                    <div className="border-t pt-4 flex justify-between items-center">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Subtotal</p>
                        <p className="text-xl font-bold">
                          ${aiSuggestion.suggested_quote.sections.reduce((total, section) => 
                            total + (section.items?.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0) || 0), 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <Button onClick={() => setQuotePreviewOpen(false)} className="bg-green-600 hover:bg-green-700">
                        <CheckCircle className="h-4 w-4 mr-2" /> Done Editing
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Regular New Job Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button className="font-semibold rounded-lg" data-testid="add-job-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Job
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Job Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="e.g., Kitchen Renovation - 123 Main St"
                    className="bg-input border-border rounded-lg"
                    data-testid="job-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed job description..."
                    className="bg-input border-border rounded-lg"
                    rows={3}
                  />
                </div>

                {/* Supplier / PO details (My Project Agent) */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier</Label>
                    <Select
                      value={formData.supplier_id || 'none'}
                      onValueChange={(val) => {
                        if (val === 'none') {
                          setFormData({ ...formData, supplier_id: '', supplier_name: '' });
                        } else {
                          const s = suppliers.find((x) => x.id === val);
                          setFormData({ ...formData, supplier_id: val, supplier_name: s?.name || '' });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-input border-border rounded-lg" data-testid="job-supplier">
                        <SelectValue placeholder="Who sent the PO?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>PO Reference</Label>
                    <Input
                      value={formData.po_reference}
                      onChange={(e) => setFormData({ ...formData, po_reference: e.target.value })}
                      placeholder="e.g., PO-99887"
                      className="bg-input border-border rounded-lg"
                      data-testid="job-po-reference"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>On-site Date (booking)</Label>
                  <Input
                    type="date"
                    value={formData.on_site_date}
                    onChange={(e) => setFormData({ ...formData, on_site_date: e.target.value })}
                    className="bg-input border-border rounded-lg"
                    data-testid="job-on-site-date"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Site Contact Name *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      className="bg-input border-border rounded-lg"
                      data-testid="job-client-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Site Contact Phone</Label>
                    <Input
                      value={formData.client_phone}
                      onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Site Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="bg-input border-border rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Site Address *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="bg-input border-border rounded-lg"
                    data-testid="job-address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Budget ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="bg-input border-border rounded-lg"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg">Cancel</Button>
                  <Button type="submit" className="font-semibold rounded-lg" data-testid="job-submit">
                    {editingJob ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-input border-border rounded-lg"
            data-testid="job-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-input border-border rounded-lg">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Briefcase className="h-12 w-12 mb-4" />
          <p>No jobs found</p>
        </div>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {filteredJobs.map((job) => {
                const StatusIcon = statusConfig[job.status]?.icon || Clock;
                const urgency = getUrgency(job);
                return (
                  <div
                    key={job.id}
                    className={`p-4 pl-5 border-l-4 ${urgency.border} hover:bg-secondary/30 cursor-pointer transition-colors`}
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    data-testid={`job-row-${job.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-foreground truncate">{job.title}</h3>
                          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${statusConfig[job.status]?.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {statusConfig[job.status]?.label}
                          </span>
                          {urgency.label && (
                            <span
                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${urgency.badge}`}
                              data-testid={`job-urgency-${job.id}`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${urgency.dot}`} />
                              {urgency.label}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {job.address}
                          </span>
                          <span className="flex items-center gap-1">
                            <Briefcase className="h-3.5 w-3.5" />
                            {job.client_name}
                          </span>
                          {job.supplier_name && (
                            <span className="flex items-center gap-1 text-primary">
                              <Building2 className="h-3.5 w-3.5" />
                              {job.supplier_name}
                            </span>
                          )}
                          {job.po_reference && (
                            <span className="text-xs font-mono bg-secondary/50 px-2 py-0.5 rounded">
                              {job.po_reference}
                            </span>
                          )}
                          {job.on_site_date && (
                            <span className="flex items-center gap-1 text-amber-400">
                              <Calendar className="h-3.5 w-3.5" />
                              On-site {format(new Date(job.on_site_date), 'MMM d, yyyy')}
                            </span>
                          )}
                          {job.budget && (
                            <span className="flex items-center gap-1 font-mono">
                              <DollarSign className="h-3.5 w-3.5" />
                              {job.budget.toLocaleString()}
                            </span>
                          )}
                          {job.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {format(new Date(job.start_date), 'MMM d, yyyy')}
                            </span>
                          )}
                          {job.assigned_contractors?.length > 0 && (
                            <span className="text-xs bg-secondary/50 px-2 py-0.5 rounded">
                              {job.assigned_contractors.length} contractor{job.assigned_contractors.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {job.links?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {job.links.map((l, i) => (
                              <a
                                key={i}
                                href={l.url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                              >
                                <ExternalLink className="h-3 w-3" /> {l.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {user?.role !== 'trade' && (
                          <>
                            {/* Inline Status Change */}
                            <Select 
                              value={job.status} 
                              onValueChange={(v) => handleStatusChange({ stopPropagation: () => {} }, job.id, v)}
                            >
                              <SelectTrigger 
                                className="w-[130px] h-8 text-xs bg-background" 
                                onClick={(e) => e.stopPropagation()}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {statusOptions.map(s => (
                                  <SelectItem key={s} value={s}>{statusConfig[s]?.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={(e) => handleEdit(e, job)}
                              className="h-8 w-8"
                              data-testid={`edit-job-${job.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={(e) => handleDelete(e, job.id)}
                              className="h-8 w-8 text-destructive"
                              data-testid={`delete-job-${job.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JobsPage;
